import { BaseClient } from '../base-client';
import type {
  CreateSystemRoleInput,
  ListSystemRolesQuery,
  PatchSystemRoleInput,
  SystemRole,
} from './types';

type One = { data: SystemRole };
type ListResponse = { data: SystemRole[]; meta: { total: number; limit: number; offset: number } };

export class SystemRolesClient extends BaseClient {
  list(query: ListSystemRolesQuery = {}): Promise<ListResponse> {
    return this.request('/api/system-roles', { query: normalize(query) });
  }

  /** Batch fetch system roles by exactly the referenced ids — bounded,
   *  never capped. See `BaseClient.findByIdsAt`. */
  findByIds(ids: readonly string[]): Promise<ListResponse> {
    return this.findByIdsAt<SystemRole>('/api/system-roles/find', ids);
  }

  get(id: string): Promise<One> {
    return this.request(`/api/system-roles/${encodeURIComponent(id)}`);
  }

  create(input: CreateSystemRoleInput): Promise<One> {
    return this.request('/api/system-roles', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchSystemRoleInput): Promise<One> {
    return this.request(`/api/system-roles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/system-roles/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/system-roles/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
